import React, { useState, useEffect } from 'react';
import { axiosInstance } from '../App';
import { toast } from 'sonner';
import { Mail, MessageSquare, Bell, X, Check, Lock, Eye, EyeOff, Settings, User, Shield, ShieldCheck, Server, Database, Key, Trash2, AlertTriangle, Phone, ArrowUpCircle, CreditCard } from 'lucide-react';
import './AccountSettings.css';

const AccountSettings = ({ isOpen, onClose, user, onLogout, onUserUpdate }) => {
  const [activeTab, setActiveTab] = useState('account');
  const [notificationSettings, setNotificationSettings] = useState({
    email_enabled: true,
    sms_enabled: true,
  });
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: '',
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false,
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);
  
  // Name change state
  const [showNameChange, setShowNameChange] = useState(false);
  const [newName, setNewName] = useState('');
  const [nameChangePassword, setNameChangePassword] = useState('');
  const [savingName, setSavingName] = useState(false);
  
  // Email change state
  const [showEmailChange, setShowEmailChange] = useState(false);
  const [emailChangeStep, setEmailChangeStep] = useState(1); // 1: enter email, 2: verify code
  const [newEmail, setNewEmail] = useState('');
  const [emailChangePassword, setEmailChangePassword] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [emailChangeToken, setEmailChangeToken] = useState('');
  const [savingEmail, setSavingEmail] = useState(false);
  const [sendingCode, setSendingCode] = useState(false);
  
  // Payment method change state
  const [loadingPaymentPortal, setLoadingPaymentPortal] = useState(false);

  useEffect(() => {
    if (isOpen) {
      fetchSettings();
      // Reset password fields when modal opens
      setPasswordData({
        current_password: '',
        new_password: '',
        confirm_password: '',
      });
    }
  }, [isOpen]);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.get('/settings/notifications');
      setNotificationSettings(response.data);
    } catch (error) {
      console.error('Failed to load notification settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveNotifications = async () => {
    if (!notificationSettings.email_enabled && !notificationSettings.sms_enabled) {
      toast.error('Please enable at least one notification method');
      return;
    }

    setSaving(true);
    try {
      await axiosInstance.put('/settings/notifications', notificationSettings);
      toast.success('Notification preferences saved!');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (passwordData.new_password !== passwordData.confirm_password) {
      toast.error('New passwords do not match');
      return;
    }

    if (passwordData.new_password.length < 6) {
      toast.error('New password must be at least 6 characters');
      return;
    }

    setSaving(true);
    try {
      await axiosInstance.post('/auth/change-password', {
        current_password: passwordData.current_password,
        new_password: passwordData.new_password,
      });
      toast.success('Password changed successfully!');
      setPasswordData({
        current_password: '',
        new_password: '',
        confirm_password: '',
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setSaving(false);
    }
  };

  // Handle name change with password verification
  const handleNameChange = async () => {
    if (!newName.trim()) {
      toast.error('Please enter a new name');
      return;
    }
    if (!nameChangePassword) {
      toast.error('Please enter your password to confirm');
      return;
    }

    setSavingName(true);
    try {
      await axiosInstance.post('/auth/change-name', {
        new_name: newName.trim(),
        password: nameChangePassword,
      });
      toast.success('Name changed successfully!');
      setShowNameChange(false);
      setNewName('');
      setNameChangePassword('');
      if (onUserUpdate) onUserUpdate();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change name');
    } finally {
      setSavingName(false);
    }
  };

  // Request email change verification code
  const handleRequestEmailChange = async () => {
    if (!newEmail.trim() || !newEmail.includes('@')) {
      toast.error('Please enter a valid email address');
      return;
    }
    if (!emailChangePassword) {
      toast.error('Please enter your password to confirm');
      return;
    }

    setSendingCode(true);
    try {
      const response = await axiosInstance.post('/auth/request-email-change', {
        new_email: newEmail.trim(),
        password: emailChangePassword,
      });
      setEmailChangeToken(response.data.token);
      setEmailChangeStep(2);
      toast.success('Verification code sent to your new email address');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send verification code');
    } finally {
      setSendingCode(false);
    }
  };

  // Verify email change code and complete the change
  const handleVerifyEmailChange = async () => {
    if (!verificationCode.trim()) {
      toast.error('Please enter the verification code');
      return;
    }

    setSavingEmail(true);
    try {
      await axiosInstance.post('/auth/verify-email-change', {
        token: emailChangeToken,
        code: verificationCode.trim(),
      });
      toast.success('Email changed successfully! Please log in with your new email.');
      setShowEmailChange(false);
      setEmailChangeStep(1);
      setNewEmail('');
      setEmailChangePassword('');
      setVerificationCode('');
      setEmailChangeToken('');
      // Log out user after email change
      setTimeout(() => {
        onLogout();
      }, 2000);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid verification code');
    } finally {
      setSavingEmail(false);
    }
  };

  // Cancel email change
  const handleCancelEmailChange = () => {
    setShowEmailChange(false);
    setEmailChangeStep(1);
    setNewEmail('');
    setEmailChangePassword('');
    setVerificationCode('');
    setEmailChangeToken('');
  };

  // Handle payment method change via Stripe Customer Portal
  const handleChangePaymentMethod = async () => {
    setLoadingPaymentPortal(true);
    try {
      const response = await axiosInstance.post('/create-customer-portal');
      // Open Stripe portal in new tab
      window.open(response.data.url, '_blank');
      toast.success('Opening payment management portal...');
    } catch (error) {
      if (error.response?.status === 400) {
        toast.error(error.response.data.detail || 'No payment method on file');
      } else {
        toast.error('Failed to open payment portal. Please try again.');
      }
    } finally {
      setLoadingPaymentPortal(false);
    }
  };

  const toggleSetting = (key) => {
    setNotificationSettings((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const togglePasswordVisibility = (field) => {
    setShowPasswords((prev) => ({
      ...prev,
      [field]: !prev[field],
    }));
  };

  const formatMemberSince = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmText !== 'DELETE') {
      toast.error('Please type DELETE to confirm');
      return;
    }

    setDeleting(true);
    try {
      await axiosInstance.delete('/auth/delete-account');
      toast.success('Account deleted successfully');
      localStorage.removeItem('token');
      if (onLogout) {
        onLogout();
      } else {
        window.location.href = '/';
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete account');
      setDeleting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" data-testid="account-settings-modal">
      <div className="modal-backdrop" onClick={onClose}></div>
      <div className="modal-content account-settings-modal">
        <div className="modal-header">
          <div className="modal-header-icon">
            <Settings size={24} />
          </div>
          <div>
            <h2>Account Settings</h2>
            <p className="modal-subtitle">Manage your account preferences</p>
          </div>
          <button className="modal-close-btn" onClick={onClose}>
            <X size={24} />
          </button>
        </div>

        {/* Tabs */}
        <div className="settings-tabs">
          <button
            className={`tab-btn ${activeTab === 'account' ? 'active' : ''}`}
            onClick={() => setActiveTab('account')}
          >
            <User size={18} />
            Account
          </button>
          <button
            className={`tab-btn ${activeTab === 'notifications' ? 'active' : ''}`}
            onClick={() => setActiveTab('notifications')}
          >
            <Bell size={18} />
            Notifications
          </button>
          <button
            className={`tab-btn ${activeTab === 'password' ? 'active' : ''}`}
            onClick={() => setActiveTab('password')}
          >
            <Lock size={18} />
            Password
          </button>
          <button
            className={`tab-btn ${activeTab === 'security' ? 'active' : ''}`}
            onClick={() => setActiveTab('security')}
          >
            <Shield size={18} />
            Security
          </button>
          <button
            className={`tab-btn danger ${activeTab === 'danger' ? 'active' : ''}`}
            onClick={() => setActiveTab('danger')}
          >
            <Trash2 size={18} />
            Delete
          </button>
        </div>

        {/* Account Tab */}
        {activeTab === 'account' && (
          <div className="account-info-tab">
            <div className="account-info-card">
              <div className="account-avatar">
                <User size={48} />
              </div>
              <div className="account-details">
                <h3>{user?.full_name || 'User'}</h3>
                <p className="account-email">{user?.email}</p>
                <span className={`account-type-badge ${user?.account_type}`}>
                  {user?.account_type === 'company' ? '🏢 Company Account' : '👤 Individual Account'}
                </span>
              </div>
            </div>

            <div className="member-since-section">
              <div className="member-since-badge">
                <div className="member-icon">
                  <ShieldCheck size={32} />
                </div>
                <div className="member-info">
                  <span className="member-label">MEMBER SINCE</span>
                  <span className="member-date">{formatMemberSince(user?.created_at)}</span>
                </div>
              </div>
            </div>

            <div className="subscription-info">
              <h4>Subscription Status</h4>
              <div className={`subscription-status-badge status-${user?.subscription_status}`}>
                {user?.subscription_status === 'active' && '✓ Active Subscription'}
                {user?.subscription_status === 'trial' && `🕐 Free Trial (${user?.days_remaining} days left)`}
                {user?.subscription_status === 'expired' && '✗ Subscription Expired'}
                {user?.subscription_status === 'canceling' && '⚠ Subscription Ending Soon'}
              </div>
            </div>

            {/* Account Management Section */}
            <div className="account-management-section">
              <h4>
                <Settings size={20} />
                Account Management
              </h4>
              
              {/* Change Name */}
              <div className="management-item">
                <div className="management-info">
                  <span className="management-label">Admin Name</span>
                  <span className="management-value">{user?.full_name}</span>
                </div>
                {!showNameChange ? (
                  <button className="btn-change" onClick={() => setShowNameChange(true)}>
                    Change
                  </button>
                ) : (
                  <div className="change-form">
                    <input
                      type="text"
                      placeholder="Enter new name"
                      value={newName}
                      onChange={(e) => setNewName(e.target.value)}
                      className="change-input"
                    />
                    <input
                      type="password"
                      placeholder="Enter your password to confirm"
                      value={nameChangePassword}
                      onChange={(e) => setNameChangePassword(e.target.value)}
                      className="change-input"
                    />
                    <div className="change-actions">
                      <button 
                        className="btn-cancel" 
                        onClick={() => { setShowNameChange(false); setNewName(''); setNameChangePassword(''); }}
                      >
                        Cancel
                      </button>
                      <button 
                        className="btn-confirm" 
                        onClick={handleNameChange}
                        disabled={savingName}
                      >
                        {savingName ? 'Saving...' : 'Confirm'}
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Change Email */}
              <div className="management-item">
                <div className="management-info">
                  <span className="management-label">Email Address</span>
                  <span className="management-value">{user?.email}</span>
                </div>
                {!showEmailChange ? (
                  <button className="btn-change" onClick={() => setShowEmailChange(true)}>
                    Change
                  </button>
                ) : (
                  <div className="change-form email-change-form">
                    {emailChangeStep === 1 && (
                      <>
                        <div className="step-indicator">
                          <span className="step active">1. Enter New Email</span>
                          <span className="step">2. Verify Code</span>
                        </div>
                        <input
                          type="email"
                          placeholder="Enter new email address"
                          value={newEmail}
                          onChange={(e) => setNewEmail(e.target.value)}
                          className="change-input"
                        />
                        <input
                          type="password"
                          placeholder="Enter your password to confirm"
                          value={emailChangePassword}
                          onChange={(e) => setEmailChangePassword(e.target.value)}
                          className="change-input"
                        />
                        <div className="change-actions">
                          <button className="btn-cancel" onClick={handleCancelEmailChange}>
                            Cancel
                          </button>
                          <button 
                            className="btn-confirm" 
                            onClick={handleRequestEmailChange}
                            disabled={sendingCode}
                          >
                            {sendingCode ? 'Sending...' : 'Send Verification Code'}
                          </button>
                        </div>
                      </>
                    )}
                    {emailChangeStep === 2 && (
                      <>
                        <div className="step-indicator">
                          <span className="step completed">1. Enter New Email ✓</span>
                          <span className="step active">2. Verify Code</span>
                        </div>
                        <div className="verification-message">
                          <Mail size={24} />
                          <p>A verification code has been sent to <strong>{newEmail}</strong></p>
                        </div>
                        <input
                          type="text"
                          placeholder="Enter 6-digit verification code"
                          value={verificationCode}
                          onChange={(e) => setVerificationCode(e.target.value)}
                          className="change-input verification-code-input"
                          maxLength={6}
                        />
                        <div className="change-actions">
                          <button className="btn-cancel" onClick={handleCancelEmailChange}>
                            Cancel
                          </button>
                          <button 
                            className="btn-confirm" 
                            onClick={handleVerifyEmailChange}
                            disabled={savingEmail}
                          >
                            {savingEmail ? 'Verifying...' : 'Verify & Change Email'}
                          </button>
                        </div>
                        <p className="email-change-note">
                          After verification, you will be logged out and need to sign in with your new email.
                        </p>
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Payment Method Section */}
            {user?.subscription_status === 'active' && (
              <div className="payment-method-section">
                <h4>
                  <CreditCard size={20} />
                  Payment Method
                </h4>
                <div className="payment-info">
                  <p>Manage your payment method, view invoices, and update billing information through our secure payment portal.</p>
                  <button
                    className="btn-change-payment"
                    onClick={handleChangePaymentMethod}
                    disabled={loadingPaymentPortal}
                  >
                    {loadingPaymentPortal ? (
                      <>Processing...</>
                    ) : (
                      <>
                        <CreditCard size={18} />
                        Change Payment Method
                      </>
                    )}
                  </button>
                  <p className="payment-note">
                    Your subscription will continue uninterrupted. The new card will be charged at your next billing date.
                  </p>
                </div>
              </div>
            )}

            {/* Upgrade Section for Company Accounts */}
            {user?.account_type === 'company' && (
              <div className="upgrade-section">
                <h4>
                  <ArrowUpCircle size={20} />
                  Upgrade Your Plan
                </h4>
                <div className="current-plan-info">
                  <span className="plan-label">Current Employee Limit:</span>
                  <span className="plan-value">{user?.employee_limit || 500} employees</span>
                </div>
                <p className="upgrade-description">
                  Need more employee slots? We offer custom enterprise plans for larger organizations.
                </p>
                <div className="upgrade-contact-box">
                  <div className="contact-header">
                    <span>Contact us to upgrade:</span>
                  </div>
                  <div className="contact-methods">
                    <a href="mailto:info@southwesttrainingcenter.org?subject=Account Upgrade Request&body=Hi,%0D%0A%0D%0AI would like to upgrade my company account to accommodate more employees.%0D%0A%0D%0ACurrent employee limit: 500%0D%0ADesired employee limit: [Please specify]%0D%0A%0D%0ACompany Name: [Your company name]%0D%0AAccount Email: [Your email]%0D%0A%0D%0AThank you!" 
                       className="contact-btn email-btn"
                       target="_blank"
                       rel="noopener noreferrer"
                    >
                      <Mail size={18} />
                      <span>info@southwesttrainingcenter.org</span>
                    </a>
                    <a href="tel:+13109957455" className="contact-btn phone-btn">
                      <Phone size={18} />
                      <span>(310) 995-7455</span>
                    </a>
                  </div>
                  <p className="contact-note">
                    Our team will respond within 24 hours to discuss your upgrade options.
                  </p>
                </div>
              </div>
            )}

            <div className="modal-actions">
              <button className="btn-outline" onClick={onClose}>
                Close
              </button>
            </div>
          </div>
        )}

        {/* Notifications Tab */}
        {activeTab === 'notifications' && (
          <>
            {loading ? (
              <div className="settings-loading">Loading...</div>
            ) : (
              <div className="notification-options">
                <div
                  className={`notification-option ${notificationSettings.email_enabled ? 'active' : ''}`}
                  onClick={() => toggleSetting('email_enabled')}
                  data-testid="email-toggle"
                >
                  <div className="option-icon email-icon">
                    <Mail size={28} />
                  </div>
                  <div className="option-content">
                    <h3>Email Reminders</h3>
                    <p>Receive reminder emails 90 days before permit expiration</p>
                  </div>
                  <div className={`option-toggle ${notificationSettings.email_enabled ? 'on' : 'off'}`}>
                    {notificationSettings.email_enabled ? <Check size={18} /> : null}
                  </div>
                </div>

                <div
                  className={`notification-option ${notificationSettings.sms_enabled ? 'active' : ''}`}
                  onClick={() => toggleSetting('sms_enabled')}
                  data-testid="sms-toggle"
                >
                  <div className="option-icon sms-icon">
                    <MessageSquare size={28} />
                  </div>
                  <div className="option-content">
                    <h3>SMS Reminders</h3>
                    <p>Receive text message reminders to your phone</p>
                  </div>
                  <div className={`option-toggle ${notificationSettings.sms_enabled ? 'on' : 'off'}`}>
                    {notificationSettings.sms_enabled ? <Check size={18} /> : null}
                  </div>
                </div>

                <div className="notification-summary">
                  <Bell size={16} />
                  <span>
                    {notificationSettings.email_enabled && notificationSettings.sms_enabled && 'You will receive both email and SMS reminders'}
                    {notificationSettings.email_enabled && !notificationSettings.sms_enabled && 'You will receive email reminders only'}
                    {!notificationSettings.email_enabled && notificationSettings.sms_enabled && 'You will receive SMS reminders only'}
                    {!notificationSettings.email_enabled && !notificationSettings.sms_enabled && 'Please select at least one notification method'}
                  </span>
                </div>
              </div>
            )}

            <div className="modal-actions">
              <button className="btn-outline" onClick={onClose}>
                Cancel
              </button>
              <button
                className="btn-primary"
                onClick={handleSaveNotifications}
                disabled={saving || (!notificationSettings.email_enabled && !notificationSettings.sms_enabled)}
              >
                {saving ? 'Saving...' : 'Save Preferences'}
              </button>
            </div>
          </>
        )}

        {/* Change Password Tab */}
        {activeTab === 'password' && (
          <form onSubmit={handleChangePassword} className="password-form">
            <div className="form-group">
              <label className="label">Current Password</label>
              <div className="password-input-wrapper">
                <input
                  type={showPasswords.current ? 'text' : 'password'}
                  className="input-field"
                  value={passwordData.current_password}
                  onChange={(e) => setPasswordData({ ...passwordData, current_password: e.target.value })}
                  required
                  placeholder="Enter your current password"
                  data-testid="current-password-input"
                />
                <button
                  type="button"
                  className="toggle-password-btn"
                  onClick={() => togglePasswordVisibility('current')}
                >
                  {showPasswords.current ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            <div className="form-group">
              <label className="label">New Password</label>
              <div className="password-input-wrapper">
                <input
                  type={showPasswords.new ? 'text' : 'password'}
                  className="input-field"
                  value={passwordData.new_password}
                  onChange={(e) => setPasswordData({ ...passwordData, new_password: e.target.value })}
                  required
                  minLength={6}
                  placeholder="Enter your new password"
                  data-testid="new-password-input"
                />
                <button
                  type="button"
                  className="toggle-password-btn"
                  onClick={() => togglePasswordVisibility('new')}
                >
                  {showPasswords.new ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
              <p className="field-hint">Must be at least 6 characters</p>
            </div>

            <div className="form-group">
              <label className="label">Confirm New Password</label>
              <div className="password-input-wrapper">
                <input
                  type={showPasswords.confirm ? 'text' : 'password'}
                  className="input-field"
                  value={passwordData.confirm_password}
                  onChange={(e) => setPasswordData({ ...passwordData, confirm_password: e.target.value })}
                  required
                  minLength={6}
                  placeholder="Confirm your new password"
                  data-testid="confirm-password-input"
                />
                <button
                  type="button"
                  className="toggle-password-btn"
                  onClick={() => togglePasswordVisibility('confirm')}
                >
                  {showPasswords.confirm ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            <div className="modal-actions">
              <button type="button" className="btn-outline" onClick={onClose}>
                Cancel
              </button>
              <button
                type="submit"
                className="btn-primary"
                disabled={saving}
              >
                {saving ? 'Changing...' : 'Change Password'}
              </button>
            </div>
          </form>
        )}

        {/* Security Tab */}
        {activeTab === 'security' && (
          <div className="security-tab">
            <div className="security-certificate">
              <div className="certificate-header">
                <div className="certificate-seal">
                  <ShieldCheck size={48} />
                </div>
                <div className="certificate-title">
                  <h3>Security Certificate</h3>
                  <p>Your data is protected by industry-standard security</p>
                </div>
              </div>

              <div className="security-features">
                <div className="security-feature">
                  <div className="feature-icon ssl">
                    <Lock size={24} />
                  </div>
                  <div className="feature-content">
                    <h4>SSL/TLS Encryption</h4>
                    <p>All data transmitted between your browser and our servers is encrypted using 256-bit SSL/TLS encryption</p>
                  </div>
                  <span className="feature-status active">✓ Active</span>
                </div>

                <div className="security-feature">
                  <div className="feature-icon password">
                    <Key size={24} />
                  </div>
                  <div className="feature-content">
                    <h4>Secure Password Storage</h4>
                    <p>Passwords are hashed using bcrypt algorithm and never stored in plain text</p>
                  </div>
                  <span className="feature-status active">✓ Active</span>
                </div>

                <div className="security-feature">
                  <div className="feature-icon database">
                    <Database size={24} />
                  </div>
                  <div className="feature-content">
                    <h4>Encrypted Database</h4>
                    <p>Your permit information is stored in a secure, encrypted MongoDB database with restricted access</p>
                  </div>
                  <span className="feature-status active">✓ Active</span>
                </div>

                <div className="security-feature">
                  <div className="feature-icon server">
                    <Server size={24} />
                  </div>
                  <div className="feature-content">
                    <h4>Secure Cloud Infrastructure</h4>
                    <p>Hosted on enterprise-grade cloud servers with 24/7 monitoring, firewalls, and DDoS protection</p>
                  </div>
                  <span className="feature-status active">✓ Active</span>
                </div>

                <div className="security-feature">
                  <div className="feature-icon auth">
                    <Shield size={24} />
                  </div>
                  <div className="feature-content">
                    <h4>JWT Authentication</h4>
                    <p>Secure token-based authentication ensures only you can access your account</p>
                  </div>
                  <span className="feature-status active">✓ Active</span>
                </div>
              </div>

              <div className="security-footer">
                <div className="trust-badges">
                  <div className="trust-badge">
                    <ShieldCheck size={20} />
                    <span>Data Protected</span>
                  </div>
                  <div className="trust-badge">
                    <Lock size={20} />
                    <span>Secure Connection</span>
                  </div>
                  <div className="trust-badge">
                    <Database size={20} />
                    <span>Encrypted Storage</span>
                  </div>
                </div>
                <p className="security-notice">
                  Southwest Training Center Permit Tracker is committed to protecting your personal and permit information. 
                  We employ industry-leading security measures to ensure your data remains safe and confidential.
                </p>
              </div>
            </div>

            <div className="modal-actions">
              <button className="btn-outline" onClick={onClose}>
                Close
              </button>
            </div>
          </div>
        )}

        {/* Danger Zone Tab - Delete Account */}
        {activeTab === 'danger' && (
          <div className="danger-zone-tab">
            <div className="danger-zone-warning">
              <AlertTriangle size={48} className="danger-icon" />
              <h3>Delete Account</h3>
              <p>This action is <strong>permanent</strong> and cannot be undone.</p>
            </div>

            <div className="danger-zone-info">
              <h4>What happens when you delete your account:</h4>
              <ul>
                <li>❌ All your permit records will be permanently deleted</li>
                <li>❌ All qualification history and documents will be removed</li>
                <li>❌ Your notification preferences will be erased</li>
                {user?.account_type === 'company' && (
                  <>
                    <li>❌ <strong>All employee accounts</strong> under your company will be deleted</li>
                    <li>❌ <strong>All employee permits</strong> and data will be permanently removed</li>
                    <li>❌ Your subscription will be cancelled immediately</li>
                  </>
                )}
                <li>❌ You will lose access to your account permanently</li>
              </ul>
            </div>

            {!showDeleteConfirm ? (
              <div className="danger-zone-actions">
                <button 
                  className="btn-danger-outline"
                  onClick={() => setShowDeleteConfirm(true)}
                >
                  <Trash2 size={18} />
                  I want to delete my account
                </button>
              </div>
            ) : (
              <div className="delete-confirm-section">
                <div className="delete-confirm-box">
                  <p>To confirm deletion, type <strong>DELETE</strong> below:</p>
                  <input
                    type="text"
                    value={deleteConfirmText}
                    onChange={(e) => setDeleteConfirmText(e.target.value.toUpperCase())}
                    placeholder="Type DELETE to confirm"
                    className="delete-confirm-input"
                  />
                  <div className="delete-confirm-actions">
                    <button 
                      className="btn-secondary"
                      onClick={() => {
                        setShowDeleteConfirm(false);
                        setDeleteConfirmText('');
                      }}
                    >
                      Cancel
                    </button>
                    <button 
                      className="btn-danger"
                      onClick={handleDeleteAccount}
                      disabled={deleteConfirmText !== 'DELETE' || deleting}
                    >
                      {deleting ? 'Deleting...' : 'Delete My Account Forever'}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AccountSettings;
